import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import React from "react";
import { Button } from "@/components/ui/button";
import { getPlanNameFromKey } from "../Pricing/plans";
import Icon from "../Core/Icons";
import { ICONS } from "../Core/Icons";
import { useUsageLimits } from "@/hooks/pricing/useUsageLimits";
import { UsageText } from "../Pricing/usage-text";
import { dark } from "@clerk/themes";
import { SettingsIcon } from "lucide-react";
import { useSession, useUser, useAuth, useClerk } from "@clerk/clerk-react";

const UserMenu = React.memo(() => {
  const { user } = useUser();
  const { signOut } = useAuth();

  const { buildUserProfileUrl } = useClerk();

  const rawSubscriptionTier = usePlanInfoStore(
    (state) => state.subscriptionTier
  );
  const setShowUpgradeModal = usePlanInfoStore(
    (state) => state.setShowUpgradeModal
  );
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);
  const { session } = useSession();
  const { remainingUsage } = useUsageLimits();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="select-none focus:outline-none cursor-pointer h-full flex items-center justify-center">
        <img
          className="w-[28px] h-[28px] rounded-md border-[2px] border-solid border-primary-0"
          src={user?.imageUrl}
          crossOrigin="anonymous"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        style={{ zIndex: 10000 }}
        className="w-56"
      >
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="cursor-default flex flex-col items-start text-xs !opacity-100"
            disabled={true}
          >
            <div className="flex flex-col">{user?.fullName}</div>
            <div>{user?.primaryEmailAddress?.emailAddress}</div>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            // Delay the modal from opening until the dropdown menu is closed
            // So that the pointer events on the body are set correctly
            setTimeout(() => {
              setShowUpgradeModal(true);
              refreshPlanInfo(session);
            }, 300);
          }}
          className={cn(
            "cursor-pointer text-xs",
            !rawSubscriptionTier && "hidden"
          )}
        >
          <span className="flex items-start justify-between w-full">
            <div className="flex items-center gap-1">
              <Icon icon={ICONS.CARD} className="w-4 h-4 mx-1" />
              <div className="">My Plan</div>
            </div>
            <div className="text-text-secondary">
              {getPlanNameFromKey(rawSubscriptionTier)}
            </div>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex text-xs flex-col items-start justify-center cursor-pointer"
          onClick={() => {
            window.open(buildUserProfileUrl(), "_blank");
          }}
        >
          <div className="flex items-center gap-1">
            <SettingsIcon className="w-4 h-4 mx-1 text-[#606064]" />
            <div className="">User Settings</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            signOut({
              redirectUrl: window.location.origin,
            });
          }}
          className="cursor-pointer text-xs"
        >
          <div className="flex items-center gap-1">
            <Icon
              icon={ICONS.ARROW_RIGHT_START_ON_RECTANGLE}
              className="w-4 h-4 mx-1"
            />
            <div className="">Log Out</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="flex text-xs flex-col items-start justify-center">
          <UsageText />
          <Button
            variant="primary"
            data-tooltip-id="upgrade-plan"
            size="sm"
            data-tooltip-content={"Upgrade your plan"}
            className={cn("w-full rounded-md bg-background-CTA")}
            onClick={() => {
              setTimeout(() => {
                setShowUpgradeModal(true);
                refreshPlanInfo(session);
              }, 300);
            }}
          >
            {remainingUsage.monthlyPromptsLeft === Infinity
              ? "See Plans"
              : "Upgrade Plan"}
          </Button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export default UserMenu;
